// Fictional demo data — clearly labelled in UI. Never mistaken for real accounts.
const FREQ = { weekly: 52/12, monthly: 1, quarterly: 1/3, "half-yearly": 1/6, annual: 1/12, "one-time": 0, custom: 1 };

export function monthlyEquiv(amount, freq) {
  return Math.round((amount * (FREQ[freq] ?? 1)) * 100) / 100;
}

function inDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export const DEMO_INCOME = {
  name: "Arjun",
  monthly_income: 80000,
  salary_day: 1,
  protected_reserve: 8000,
  available_balance: 38000,
};

export const DEMO_COMMITMENTS = [
  { id: "d1", name: "Home Rent", merchant: "Landlord", category: "bill", subcategory: "Rent", amount: 18000, frequency: "monthly", next_due_date: inDays(5), payment_source: "HDFC a/c", essential_flexible: "essential", shared_individual: "individual", status: "active" },
  { id: "d2", name: "Phone EMI", merchant: "Bajaj Finserv", category: "emi", amount: 4250, frequency: "monthly", next_due_date: inDays(3), payment_source: "ICICI a/c", essential_flexible: "essential", shared_individual: "individual", status: "active", total_instalments: 12, instalments_completed: 3, total_remaining_liability: 38250 },
  { id: "d3", name: "Netflix", merchant: "Netflix", category: "subscription", subcategory: "OTT", amount: 649, frequency: "monthly", next_due_date: inDays(6), payment_source: "HDFC Credit Card", essential_flexible: "flexible", shared_individual: "individual", status: "active" },
  { id: "d4", name: "Spotify", merchant: "Spotify", category: "subscription", subcategory: "Music", amount: 119, frequency: "monthly", next_due_date: inDays(11), payment_source: "UPI", essential_flexible: "flexible", shared_individual: "individual", status: "active" },
  { id: "d5", name: "Cloud Storage", merchant: "Google One", category: "subscription", subcategory: "Cloud storage", amount: 130, frequency: "monthly", next_due_date: inDays(14), payment_source: "HDFC Credit Card", essential_flexible: "flexible", shared_individual: "individual", status: "active" },
  { id: "d6", name: "Gym Membership", merchant: "Cult.fit", category: "subscription", subcategory: "Fitness", amount: 1499, frequency: "monthly", next_due_date: inDays(9), payment_source: "UPI", essential_flexible: "flexible", shared_individual: "individual", status: "active", usage_flag: "unused" },
  { id: "d7", name: "Internet", merchant: "ACT Fibernet", category: "bill", subcategory: "Internet", amount: 999, frequency: "monthly", next_due_date: inDays(8), payment_source: "Autopay UPI", essential_flexible: "essential", shared_individual: "individual", status: "active" },
  { id: "d8", name: "Electricity (estimate)", merchant: "BESCOM", category: "bill", subcategory: "Electricity", amount: 1800, frequency: "monthly", next_due_date: inDays(12), payment_source: "UPI", essential_flexible: "essential", shared_individual: "individual", status: "active" },
  { id: "d9", name: "BNPL Instalment", merchant: "Simpl", category: "bnpl", amount: 2400, frequency: "one-time", next_due_date: inDays(4), payment_source: "UPI", essential_flexible: "flexible", shared_individual: "individual", status: "active" },
  { id: "d10", name: "Term Insurance", merchant: "HDFC Life", category: "bill", subcategory: "Insurance", amount: 12000, frequency: "annual", next_due_date: inDays(40), payment_source: "HDFC a/c", essential_flexible: "essential", shared_individual: "individual", status: "active" },
];

export function computeDemoPosition() {
  const active = DEMO_COMMITMENTS.filter((c) => c.status === "active");
  const monthly_recurring = Math.round(active.reduce((s, c) => s + monthlyEquiv(c.amount, c.frequency), 0));
  const today = new Date();
  const committed_this_month = Math.round(active.filter((c) => {
    if (!c.next_due_date) return c.frequency === "monthly" || c.frequency === "weekly";
    const dr = Math.round((new Date(c.next_due_date) - new Date()) / 86400000);
    return dr >= -3 && dr <= 31;
  }).reduce((s, c) => s + c.amount, 0));
  const income = DEMO_INCOME.monthly_income;
  const ratio = Math.round((monthly_recurring / income) * 1000) / 10;
  const reserve = DEMO_INCOME.protected_reserve;
  // due before next salary date (income cycle)
  const nxt = new Date(today.getFullYear(), today.getMonth() + (today.getDate() < DEMO_INCOME.salary_day ? 0 : 1), DEMO_INCOME.salary_day);
  const due_in_cycle = Math.round(active.filter((c) => {
    if (!c.next_due_date) return false;
    const d = new Date(c.next_due_date);
    return d >= today && d < nxt;
  }).reduce((s, c) => s + c.amount, 0));
  const safe = DEMO_INCOME.available_balance - due_in_cycle - reserve;
  return {
    monthly_income: income,
    protected_reserve: reserve,
    available_balance: DEMO_INCOME.available_balance,
    committed_this_month,
    monthly_recurring,
    commitment_ratio: ratio,
    ratio_status: ratio < 30 ? "Lightly committed" : ratio < 50 ? "Moderately committed" : ratio <= 70 ? "Highly committed" : "Heavily committed",
    due_in_cycle,
    income_before_cycle: 0,
    safe_to_spend: safe,
    safe_mode: "balance_based",
    safe_label: "Estimated Safe to Spend",
    safe_inputs: [
      { label: "Current available balance", amount: DEMO_INCOME.available_balance, sign: "+", source: "user" },
      { label: "Income arriving before next salary", amount: 0, sign: "+", source: "user" },
      { label: "Commitments due this cycle", amount: due_in_cycle, sign: "−", source: "predicted" },
      { label: "Protected reserve", amount: reserve, sign: "−", source: "user" },
    ],
    last_updated: new Date().toISOString(),
    active_count: active.length,
    breakdown: {
      essential: Math.round(active.filter((c) => c.essential_flexible === "essential" && !["emi","bnpl"].includes(c.category)).reduce((s,c)=>s+monthlyEquiv(c.amount,c.frequency),0)),
      debt: Math.round(active.filter((c) => ["emi","bnpl"].includes(c.category)).reduce((s,c)=>s+monthlyEquiv(c.amount,c.frequency),0)),
      flexible: Math.round(active.filter((c) => c.essential_flexible === "flexible" && !["emi","bnpl"].includes(c.category)).reduce((s,c)=>s+monthlyEquiv(c.amount,c.frequency),0)),
      shared: 0,
    },
  };
}

export function enrichDemo(c) {
  const dr = c.next_due_date ? Math.round((new Date(c.next_due_date) - new Date()) / 86400000) : null;
  return { ...c, monthly_equivalent: monthlyEquiv(c.amount, c.frequency), days_remaining: dr,
    remaining_instalments: c.total_instalments != null && c.instalments_completed != null ? c.total_instalments - c.instalments_completed : null };
}
